import {
  Clock, CheckCircle, XCircle, AlertCircle, FileQuestion, FolderHeart, Radio, AlertTriangle, UserCheck, Link2,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import localApiAdapter from '../../core/services/localApiAdapter';
import { dispatchHrEmployeesRefresh } from '../ressources-humaines/hrRefreshEvents';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { getBrandPhonePlaceholder, normalizeBrandPhone, SAUDI_PHONE_PREFIX } from '../../shared/utils/brandCountry';
import { calculateEOSBProvision } from '../salaires/utils/payrollSaudiFormat';
import {
  calculateTerminationEOSB,
  calculateYearsOfService,
  formatHrPhone,
  getEmployeeNationalityType,
  getSaudiContractLabel,
  normalizeContractType,
  normalizeEmployeeForSaudi,
  NITAQAT_LEVELS,
  SAUDI_CITIES,
  SAUDI_PROBATION_DAYS_DEFAULT,
  SAUDI_REGIONS,
} from '../ressources-humaines/utils/hrSaudiFormat';
import i18n from '../../i18n';

export {
  formatBrandMoney,
  getBrandLocale,
  SAUDI_CITIES,
  SAUDI_REGIONS,
  NITAQAT_LEVELS,
  SAUDI_PROBATION_DAYS_DEFAULT,
};

/** Saudi Labor Law — annual / sick leave entitlements (days). */
export const SAUDI_ANNUAL_LEAVE_DAYS = 21;
export const SAUDI_SICK_LEAVE_DAYS = 120;
export const SAUDI_WORKING_HOURS = { daily: 8, weekly: 48 };
export const SAUDI_WEEKEND_LABEL = 'الجمعة — السبت';

/** Saudi employee portal service request types (no Algerian-specific items). */
export const REQUEST_TYPES = [
  { value: 'salary_certificate', labelKey: 'req_salary_certificate' },
  { value: 'employment_certificate', labelKey: 'req_employment_certificate' },
  { value: 'bank_letter', labelKey: 'req_bank_letter' },
  { value: 'iqama_renewal', labelKey: 'req_iqama_renewal' },
  { value: 'work_permit_renewal', labelKey: 'req_work_permit_renewal' },
  { value: 'exit_reentry_visa', labelKey: 'req_exit_reentry_visa' },
  { value: 'medical_insurance_claim', labelKey: 'req_medical_insurance_claim' },
  { value: 'it_equipment', labelKey: 'req_it_equipment' },
  { value: 'transportation', labelKey: 'req_transportation' },
  { value: 'other', labelKey: 'req_other' },
];

export const LEGACY_REQUEST_TYPE_MAP = {
  hr: 'employment_certificate',
  finance: 'salary_certificate',
  admin: 'bank_letter',
  it: 'it_equipment',
};

export function normalizeRequestType(type) {
  if (!type) return '';
  const key = String(type).trim();
  if (REQUEST_TYPES.some((t) => t.value === key)) return key;
  return LEGACY_REQUEST_TYPE_MAP[key] || LEGACY_REQUEST_TYPE_MAP[key.toLowerCase()] || key;
}

export const SERVICE_STATUS = [
  { value: 'submitted', labelKey: 'svc_submitted' },
  { value: 'pending_manager', labelKey: 'svc_pending_manager' },
  { value: 'pending_hr', labelKey: 'svc_pending_hr' },
  { value: 'in_progress', labelKey: 'svc_in_progress' },
  { value: 'completed', labelKey: 'svc_completed' },
  { value: 'rejected', labelKey: 'svc_rejected' },
  { value: 'cancelled', labelKey: 'svc_cancelled' },
];

export const LEGACY_SERVICE_STATUS_MAP = {
  nouveau: 'submitted',
  en_cours: 'in_progress',
  traite: 'completed',
  rejete: 'rejected',
  annule: 'cancelled',
};

export function normalizeServiceStatus(status) {
  if (!status) return '';
  const key = String(status).trim();
  if (SERVICE_STATUS.some((s) => s.value === key)) return key;
  return LEGACY_SERVICE_STATUS_MAP[key] || LEGACY_SERVICE_STATUS_MAP[key.toLowerCase()] || key;
}

export const PRIORITY_OPTIONS = [
  { value: 'low', labelKey: 'priority_low' },
  { value: 'normal', labelKey: 'priority_normal' },
  { value: 'high', labelKey: 'priority_high' },
  { value: 'urgent', labelKey: 'priority_urgent' },
];

export const LEGACY_PRIORITY_MAP = {
  basse: 'low',
  haute: 'high',
  urgente: 'urgent',
};

export function normalizePriority(priority) {
  if (!priority) return 'normal';
  const key = String(priority).trim();
  if (PRIORITY_OPTIONS.some((p) => p.value === key)) return key;
  return LEGACY_PRIORITY_MAP[key] || LEGACY_PRIORITY_MAP[key.toLowerCase()] || key;
}

export const DOCUMENT_STATUS = [
  { value: 'actif', labelKey: 'doc_active' },
  { value: 'archive', labelKey: 'doc_archived' },
  { value: 'expire', labelKey: 'doc_expired' },
];

export const DOCUMENT_CATEGORIES = [
  { value: 'payslip', labelKey: 'cat_payslip' },
  { value: 'employment_contract', labelKey: 'cat_employment_contract' },
  { value: 'salary_certificate', labelKey: 'cat_salary_certificate' },
  { value: 'gosi_statement', labelKey: 'cat_gosi_statement' },
  { value: 'tax_clearance', labelKey: 'cat_tax_clearance' },
  { value: 'iqama', labelKey: 'cat_iqama' },
  { value: 'work_permit', labelKey: 'cat_work_permit' },
  { value: 'medical_insurance', labelKey: 'cat_medical_insurance' },
  { value: 'exit_reentry_visa', labelKey: 'cat_exit_reentry_visa' },
  { value: 'national_id', labelKey: 'cat_national_id' },
  { value: 'other', labelKey: 'cat_other' },
];

export const LEGACY_DOCUMENT_CATEGORY_MAP = {
  identity: 'national_id',
  contract: 'employment_contract',
  certificate: 'salary_certificate',
  cnas: 'gosi_statement',
  securite_sociale: 'gosi_statement',
  bulletin: 'payslip',
  paie: 'payslip',
};

export function normalizeDocumentCategory(category) {
  if (!category) return '';
  const key = String(category).trim();
  if (DOCUMENT_CATEGORIES.some((c) => c.value === key)) return key;
  return LEGACY_DOCUMENT_CATEGORY_MAP[key] || LEGACY_DOCUMENT_CATEGORY_MAP[key.toLowerCase()] || key;
}

export const ANNOUNCEMENT_STATUS = [
  { value: 'brouillon', labelKey: 'ann_draft' },
  { value: 'publie', labelKey: 'ann_published' },
  { value: 'archive', labelKey: 'ann_archived' },
];

export const ANNOUNCEMENT_CATEGORIES = [
  { value: 'policy', labelKey: 'ann_cat_policy' },
  { value: 'news', labelKey: 'ann_cat_news' },
  { value: 'event', labelKey: 'ann_cat_event' },
  { value: 'hr', labelKey: 'ann_cat_hr' },
  { value: 'holidays', labelKey: 'ann_cat_holidays' },
  { value: 'ramadan', labelKey: 'ann_cat_ramadan' },
  { value: 'wps', labelKey: 'ann_cat_wps' },
  { value: 'prayer', labelKey: 'ann_cat_prayer' },
];

export const SAUDI_LEAVE_TYPES = [
  { value: 'annual', labelKey: 'leave_type_annual' },
  { value: 'sick', labelKey: 'leave_type_sick' },
  { value: 'maternity', labelKey: 'leave_type_maternity' },
  { value: 'hajj', labelKey: 'leave_type_hajj' },
  { value: 'bereavement', labelKey: 'leave_type_bereavement' },
  { value: 'marriage', labelKey: 'leave_type_marriage' },
  { value: 'paternity', labelKey: 'leave_type_paternity' },
  { value: 'unpaid', labelKey: 'leave_type_unpaid' },
];

export const SAUDI_LEAVE_ENTITLEMENTS = {
  annual: SAUDI_ANNUAL_LEAVE_DAYS,
  sick: SAUDI_SICK_LEAVE_DAYS,
  maternity: 70,
};

export const LEGACY_LEAVE_TYPE_MAP = {
  conge_annuel: 'annual',
  'Congé Annuel': 'annual',
  'Annual Leave': 'annual',
  conge_maladie: 'sick',
  'Congé Maladie': 'sick',
  'Sick Leave': 'sick',
  conge_maternite: 'maternity',
  'Congé Maternité': 'maternity',
  'Maternity Leave': 'maternity',
  conge_hajj: 'hajj',
  'Hajj Leave': 'hajj',
  conge_deuil: 'bereavement',
  conge_mariage: 'marriage',
  conge_paternite: 'paternity',
  conge_sans_solde: 'unpaid',
  'Congé Sans Solde': 'unpaid',
  'Unpaid Leave': 'unpaid',
  RTT: 'unpaid',
};

export function normalizeLeaveType(type) {
  if (!type) return '';
  const key = String(type).trim();
  if (SAUDI_LEAVE_TYPES.some((t) => t.value === key)) return key;
  return LEGACY_LEAVE_TYPE_MAP[key] || LEGACY_LEAVE_TYPE_MAP[key.toLowerCase?.()] || key;
}

export function getLeaveTypeLabel(type, pl) {
  const normalized = normalizeLeaveType(type);
  const match = SAUDI_LEAVE_TYPES.find((t) => t.value === normalized);
  return match ? pl(match.labelKey) : (type || '—');
}

export const LEAVE_STATUS = [
  { value: 'pending_manager', labelKey: 'leave_status_pending_manager' },
  { value: 'pending_hr', labelKey: 'leave_status_pending_hr' },
  { value: 'approved', labelKey: 'leave_status_approved' },
  { value: 'rejected', labelKey: 'leave_status_rejected' },
  { value: 'cancelled', labelKey: 'leave_status_cancelled' },
];

export const LEGACY_LEAVE_STATUS_MAP = {
  en_attente: 'pending_manager',
  approuve: 'approved',
  rejete: 'rejected',
  annule: 'cancelled',
  Pending: 'pending_manager',
  Approved: 'approved',
  Rejected: 'rejected',
};

export function normalizeLeaveStatus(status) {
  if (!status) return '';
  const key = String(status).trim();
  if (LEAVE_STATUS.some((s) => s.value === key)) return key;
  return LEGACY_LEAVE_STATUS_MAP[key] || LEGACY_LEAVE_STATUS_MAP[key.toLowerCase?.()] || key;
}

export function statusBadge(status, pl, map = SERVICE_STATUS) {
  const normalized = map === LEAVE_STATUS
    ? normalizeLeaveStatus(status)
    : map === SERVICE_STATUS
      ? normalizeServiceStatus(status)
      : status;
  const colors = {
    actif: 'bg-green-500/20 text-green-300 border-green-500/30',
    archive: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    expire: 'bg-red-500/20 text-red-300 border-red-500/30',
    nouveau: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    en_cours: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    traite: 'bg-green-500/20 text-green-300 border-green-500/30',
    rejete: 'bg-red-500/20 text-red-300 border-red-500/30',
    annule: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    brouillon: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    publie: 'bg-green-500/20 text-green-300 border-green-500/30',
    en_attente: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    approuve: 'bg-green-500/20 text-green-300 border-green-500/30',
    submitted: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    pending_manager: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    pending_hr: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    in_progress: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    completed: 'bg-green-500/20 text-green-300 border-green-500/30',
    approved: 'bg-green-500/20 text-green-300 border-green-500/30',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
    cancelled: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  };
  const labelKey = map.find((s) => s.value === normalized)?.labelKey
    || map.find((s) => s.value === status)?.labelKey;
  const colorKey = colors[normalized] ? normalized : status;
  return (
    <span className={`px-2 py-1 rounded-full text-xs border ${colors[colorKey] || 'bg-white/10 text-white/70 border-white/20'}`}>
      {labelKey ? pl(labelKey) : status}
    </span>
  );
}

export function countByStatus(records, statusField, statusValue) {
  return records.filter((r) => r[statusField] === statusValue).length;
}

export function countByNormalizedStatus(records, statusField, targetStatus, normalizer) {
  return records.filter((r) => normalizer(r[statusField]) === targetStatus).length;
}

/**
 * ar-SA date formatting with optional Hijri (Umm al-Qura).
 * @param {string|Date} value
 * @param {string|object} [languageOrOpts] — i18n language or `{ showHijri, hijri }`
 * @param {object} [opts] — `{ hijri, showHijri }`
 */
export function formatPortalDate(value, languageOrOpts, opts = {}) {
  let showHijri = false;
  let language = 'ar';

  if (typeof languageOrOpts === 'object' && languageOrOpts !== null) {
    showHijri = !!(languageOrOpts.showHijri ?? languageOrOpts.hijri);
  } else if (typeof languageOrOpts === 'string') {
    language = languageOrOpts;
    showHijri = !!(opts.hijri ?? opts.showHijri);
  }

  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  const locale = getBrandLocale(language);
  const gregorian = d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const includeHijri = showHijri || getPortalHijriPref();
  if (!includeHijri) return gregorian;

  try {
    const hijri = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
    return `${gregorian} (${hijri})`;
  } catch {
    return gregorian;
  }
}

export function getPortalHijriPref() {
  try {
    const raw = localStorage.getItem('employee_portal_prefs');
    if (raw) {
      const prefs = JSON.parse(raw);
      return prefs.showHijriDates === true || prefs.useHijriCalendar === '1';
    }
  } catch { /* ignore */ }
  return false;
}

export function setPortalHijriPref(show) {
  try {
    const raw = localStorage.getItem('employee_portal_prefs');
    const prefs = raw ? JSON.parse(raw) : {};
    localStorage.setItem('employee_portal_prefs', JSON.stringify({
      ...prefs,
      showHijriDates: !!show,
      useHijriCalendar: show ? '1' : '0',
    }));
  } catch { /* ignore */ }
}

function leaveDays(record) {
  const days = record?.days ?? record?.duration_days;
  const n = Number(days);
  if (Number.isFinite(n) && n > 0) return n;
  const start = record?.startDate || record?.start_date;
  const end = record?.endDate || record?.end_date;
  if (start && end) {
    const diff = Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  }
  return 0;
}

function isApprovedLeave(record) {
  return normalizeLeaveStatus(record?.status) === 'approved';
}

export function computeSaudiLeaveBalances(records) {
  const approved = (records || []).filter(isApprovedLeave);
  const sumDays = (type) => approved
    .filter((r) => normalizeLeaveType(r.leaveType || r.type || r.leave_type) === type)
    .reduce((sum, r) => sum + leaveDays(r), 0);

  const annualUsed = sumDays('annual');
  const sickUsed = sumDays('sick');
  const maternityUsed = sumDays('maternity');
  const sickFull = Math.min(sickUsed, 30);
  const sickHalf = Math.min(Math.max(sickUsed - 30, 0), 60);
  const sickUnpaid = Math.max(sickUsed - 90, 0);

  return {
    annual: {
      entitled: SAUDI_LEAVE_ENTITLEMENTS.annual,
      used: annualUsed,
      remaining: Math.max(0, SAUDI_LEAVE_ENTITLEMENTS.annual - annualUsed),
    },
    sick: {
      entitled: SAUDI_LEAVE_ENTITLEMENTS.sick,
      used: sickUsed,
      remaining: Math.max(0, SAUDI_LEAVE_ENTITLEMENTS.sick - sickUsed),
      phases: {
        fullPaid: { limit: 30, used: sickFull, remaining: Math.max(0, 30 - sickFull) },
        halfPaid: { limit: 60, used: sickHalf, remaining: Math.max(0, 60 - sickHalf) },
        unpaid: { limit: 30, used: sickUnpaid, remaining: Math.max(0, 30 - sickUnpaid) },
      },
    },
    maternity: {
      entitled: SAUDI_LEAVE_ENTITLEMENTS.maternity,
      used: maternityUsed,
      remaining: Math.max(0, SAUDI_LEAVE_ENTITLEMENTS.maternity - maternityUsed),
      eligible: maternityUsed < SAUDI_LEAVE_ENTITLEMENTS.maternity,
    },
  };
}

export function getRequestTypeLabel(type, pl) {
  const normalized = normalizeRequestType(type);
  const match = REQUEST_TYPES.find((t) => t.value === normalized);
  return match ? pl(match.labelKey) : (type || '—');
}

export function sessionIncludesEmployee(record, employeeId) {
  if (!employeeId) return true;
  const target = String(employeeId);
  const participants = record?.participants || [];
  return participants.some((p) => {
    if (typeof p === 'object' && p !== null) {
      return [p.id, p.employeeId, p.employee_id, p.mat]
        .filter((v) => v != null && v !== '')
        .some((v) => String(v) === target);
    }
    return String(p) === target;
  });
}

export function defaultStats(records, config) {
  const { totalIcon = FileQuestion, fields } = config;
  return sliceQawaemStats([
    { titleKey: 'stat_total', value: records.length, icon: totalIcon, color: 'from-teal-500 to-teal-600', variant: 'copper' },
    ...fields.map((f) => ({
      titleKey: f.titleKey,
      value: f.countFn
        ? f.countFn(records)
        : countByStatus(records, f.field, f.value),
      icon: f.icon,
      color: f.color,
      variant: f.variant || 'forest',
    })),
  ], 4);
}

export const PAGE_STAT_ICONS = {
  pending: Clock,
  done: CheckCircle,
  rejected: XCircle,
  progress: AlertCircle,
  documents: FolderHeart,
  announcements: Radio,
};

/** Map legacy DB row (snake_case) to Saudi-normalized portal profile. */
export function normalizePortalProfile(record = {}) {
  const mapped = {
    ...record,
    matricule: record.matricule || record.mat || record.employee_id || '',
    nationalId: record.national_id || record.nationalId || '',
    iqamaNumber: record.iqama_number || record.iqamaNumber || '',
    iqamaExpiry: record.iqama_expiry || record.iqamaExpiry || '',
    passportNumber: record.passeport || record.passportNumber || record.passport || '',
    passportExpiry: record.passport_expiry || record.passportExpiry || '',
    gosiNumber: record.gosi_number || record.securite_sociale || record.gosiNumber || '',
    workPermitNumber: record.work_permit_number || record.num_permis || record.workPermitNumber || '',
    workPermitExpiry: record.work_permit_expiry || record.workPermitExpiry || '',
    medicalInsuranceNumber: record.medical_insurance_number || record.medicalInsuranceNumber || '',
    medicalInsuranceExpiry: record.medical_insurance_expiry || record.medicalInsuranceExpiry || '',
    nitaqatCategory: record.nitaqat_category || record.nitaqatCategory || '',
    salaireBase: record.salaire_base ?? record.salaireBase ?? 0,
    salaireNetMensuel: record.salaire_net_mensuel ?? record.salaireNetMensuel ?? 0,
    typeContrat: record.type_contrat || record.typeContrat || '',
    dateRecrutement: record.date_recrutement || record.dateRecrutement || '',
    periodeEssai: record.periode_essai || record.periodeEssai || String(SAUDI_PROBATION_DAYS_DEFAULT),
    finEssai: record.fin_essai || record.finEssai || '',
    indemniteTransport: record.indemnite_transport ?? record.indemniteTransport ?? 0,
    indemnitePanier: record.indemnite_panier ?? record.indemnitePanier ?? 0,
    indemniteExperience: record.indemnite_experience ?? record.indemniteExperience ?? 0,
    primeResponsabilite: record.prime_responsabilite ?? record.primeResponsabilite ?? 0,
    primeRendementIndividuel: record.prime_rendement_individuel ?? record.primeRendementIndividuel ?? 0,
    primeRendementCollectif: record.prime_rendement_collectif ?? record.primeRendementCollectif ?? 0,
    soldeCongesAnnuel: record.solde_conges_annuel ?? record.soldeCongesAnnuel,
    soldeCongesMaladie: record.solde_conges_maladie ?? record.soldeCongesMaladie,
    region: record.region || record.ville || '',
  };
  const normalized = normalizeEmployeeForSaudi(mapped);
  return {
    ...normalized,
    annualLeaveBalance: SAUDI_ANNUAL_LEAVE_DAYS,
    sickLeaveBalance: SAUDI_SICK_LEAVE_DAYS,
    soldeCongesAnnuel: normalized.soldeCongesAnnuel ?? SAUDI_ANNUAL_LEAVE_DAYS,
    soldeCongesMaladie: normalized.soldeCongesMaladie ?? SAUDI_SICK_LEAVE_DAYS,
  };
}

export function formatPortalMoney(amount) {
  return formatBrandMoney(amount);
}

export function formatPortalPhone(value) {
  return formatHrPhone(value) || '—';
}

export function getPortalPhonePlaceholder() {
  return getBrandPhonePlaceholder();
}

export function normalizePortalPhone(value) {
  return normalizeBrandPhone(value);
}

export function validatePortalPhone(value) {
  const raw = (value || '').toString().trim();
  if (!raw) return true;
  return normalizeBrandPhone(raw).startsWith(SAUDI_PHONE_PREFIX);
}

export function calculatePortalBenefits(profile = {}) {
  const fields = [
    'indemniteTransport', 'indemnitePanier', 'indemniteExperience', 'primeResponsabilite',
  ];
  return fields.reduce((sum, key) => sum + (parseFloat(profile[key]) || 0), 0);
}

export function calculatePortalBonus(profile = {}) {
  return (parseFloat(profile.primeRendementIndividuel) || 0)
    + (parseFloat(profile.primeRendementCollectif) || 0);
}

export function calculatePortalEosbBalance(profile = {}) {
  const basic = parseFloat(profile.salaireBase) || 0;
  const start = profile.dateRecrutement;
  if (!basic || !start) return 0;
  const { grossEOSB } = calculateTerminationEOSB({
    basicSalary: basic,
    startDate: start,
    endDate: new Date(),
    departureType: 'contract_end',
  });
  return grossEOSB;
}

export function calculatePortalEosbAccrual(profile = {}) {
  return calculateEOSBProvision(parseFloat(profile.salaireBase) || 0);
}

export function getPortalGosiStatusKey(profile = {}) {
  const gosi = profile.gosiNumber || profile.securite_sociale;
  return gosi ? 'gosi_registered' : 'gosi_not_registered';
}

export function getPortalNitaqatLabel(category, t) {
  const entry = NITAQAT_LEVELS.find((n) => n.value === category);
  if (!entry) return category || '—';
  return t(`modules.employee_portal.shared.${entry.labelKey}`, { defaultValue: category });
}

export function getPortalContractLabel(typeContrat, t) {
  return getSaudiContractLabel(normalizeContractType(typeContrat), t);
}

export function isPortalSaudiEmployee(profile = {}) {
  return getEmployeeNationalityType(profile) === 'saudi';
}

export { getEmployeeNationalityType };

export function getPortalGreetingKey() {
  const hour = new Date().getHours();
  if (hour < 12) return 'greeting_morning';
  if (hour < 17) return 'greeting_afternoon';
  if (hour < 21) return 'greeting_evening';
  return 'greeting_night';
}

export function getPortalYearsOfService(profile = {}) {
  return calculateYearsOfService(profile.dateRecrutement);
}

export const PORTAL_FAMILY_STATUS = [
  { value: 'celibataire', labelKey: 'family_single' },
  { value: 'marie', labelKey: 'family_married' },
  { value: 'divorce', labelKey: 'family_divorced' },
  { value: 'veuf', labelKey: 'family_widowed' },
  { value: 'Célibataire', labelKey: 'family_single' },
  { value: 'Marié(e)', labelKey: 'family_married' },
  { value: 'Divorcé(e)', labelKey: 'family_divorced' },
  { value: 'Veuf(ve)', labelKey: 'family_widowed' },
];

const ep = (key, opts) => i18n.t(`modules.employee_portal.pages.common.${key}`, opts);

function isPortalDevHost() {
  if (typeof window === 'undefined') return false;
  return process.env.NODE_ENV === 'development'
    || /localhost|127\.0\.0\.1/.test(window.location.hostname);
}

/** Persist linked employee id/email so portal scope resolves without re-login. */
export function persistPortalEmployeeSession(row, fallbackEmail = '') {
  if (!row?.id) return;
  const email = String(row.email || fallbackEmail || '').trim();
  const patch = {
    employee_id: row.id,
    employeeId: row.id,
    ...(email ? { email } : {}),
    username: `${row.prenom || ''} ${row.nom || ''}`.trim() || undefined,
  };
  const keys = ['qawaem_user', 'siterp_user', 'user', 'invoit_user', 'qawaem_admin_user'];
  let stored = false;
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      localStorage.setItem(key, JSON.stringify({ ...parsed, ...patch }));
      stored = true;
      break;
    } catch (_) { /* ignore */ }
  }
  if (!stored) {
    localStorage.setItem('qawaem_user', JSON.stringify({
      email: email || `demo-${row.id}@qawaem.test`,
      ...patch,
    }));
  }
}

function resolveScopeAlertMessage(error) {
  const fallback = ep('employee_scope_required', {
    defaultValue: 'تعذّر تحديد الموظف — تأكد من ربط حسابك بسجل الموظف أو أعد تسجيل الدخول',
  });
  if (!error) return fallback;
  const text = String(error);
  if (text.startsWith('modules.') || text.includes('.pages.common.')) return fallback;
  return text;
}

/** Banner when portal pages cannot resolve the logged-in employee. */
export function EmployeePortalScopeAlert({ error = null }) {
  const message = resolveScopeAlertMessage(error);
  return (
    <div
      className="mb-4 flex items-start gap-3 rounded-2xl border-2 border-amber-600/50 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-50"
      role="alert"
      data-testid="employee-portal-scope-alert"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" aria-hidden />
      <p className="text-sm font-medium leading-relaxed">{message}</p>
    </div>
  );
}

/** Full empty panel when login is not linked to an HR employee — with one-click fix. */
export function EmployeePortalUnlinkedState({ email = '', name = '', onLinked = null }) {
  const [linking, setLinking] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const devMode = isPortalDevHost();

  const finishLink = (row, resolvedEmail) => {
    persistPortalEmployeeSession(row, resolvedEmail);
    setLinkSuccess(true);
    dispatchHrEmployeesRefresh();
    onLinked?.(row);
  };

  const handleLink = async () => {
    const lookupEmail = String(email || '').trim();
    if (!lookupEmail) return;
    setLinking(true);
    setLinkError(null);
    try {
      const res = await localApiAdapter.post('/api/employee-portal/profile/link-account', {
        email: lookupEmail,
        name: name || undefined,
      });
      const row = res?.data ?? res;
      if (!row?.id) {
        throw new Error(ep('unlink_create_error'));
      }
      finishLink(row, lookupEmail);
    } catch (err) {
      setLinkError(err?.message || ep('unlink_create_error'));
    } finally {
      setLinking(false);
    }
  };

  const handleSeedDemo = async () => {
    setSeeding(true);
    setLinkError(null);
    try {
      const lookupEmail = String(email || '').trim();
      const res = await localApiAdapter.post('/api/employee-portal/profile/seed-demo', {
        email: lookupEmail || undefined,
        name: name || undefined,
      });
      const row = res?.data ?? res;
      if (!row?.id) {
        throw new Error(ep('unlink_seed_error', { defaultValue: ep('unlink_create_error') }));
      }
      finishLink(row, row.email || lookupEmail);
    } catch (err) {
      setLinkError(err?.message || ep('unlink_seed_error', { defaultValue: 'تعذّر إنشاء بيانات تجريبية' }));
    } finally {
      setSeeding(false);
    }
  };

  const busy = linking || seeding;

  return (
    <div
      className="rounded-3xl border border-white/20 bg-white/10 backdrop-blur-lg p-8 text-center max-w-xl mx-auto"
      data-testid="employee-portal-unlinked-state"
    >
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-teal-500 to-teal-700">
        <UserCheck className="h-8 w-8 text-white" aria-hidden />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">{ep('unlink_title')}</h2>
      <p className="text-white/70 text-sm mb-4">{ep('unlink_hint')}</p>
      {email ? (
        <p className="text-white/90 text-sm mb-6">
          <span className="text-white/50">{ep('unlink_email_label')}: </span>
          <span className="font-mono direction-ltr inline-block">{email}</span>
        </p>
      ) : devMode ? (
        <p className="text-white/60 text-sm mb-6">{ep('unlink_no_email_dev', { defaultValue: 'لا يوجد بريد مسجّل — يمكنك إنشاء ملف تجريبي ببيانات عشوائية للاختبار.' })}</p>
      ) : null}
      {linkSuccess ? (
        <p className="text-emerald-300 text-sm font-medium">{ep('unlink_create_success')}</p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center flex-wrap">
          {email ? (
            <button
              type="button"
              disabled={busy}
              onClick={handleLink}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-700 text-white font-semibold disabled:opacity-50"
            >
              <Link2 size={18} />
              {linking ? ep('unlink_creating') : ep('unlink_create_btn')}
            </button>
          ) : null}
          {devMode ? (
            <button
              type="button"
              disabled={busy}
              onClick={handleSeedDemo}
              data-testid="employee-portal-seed-demo-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold disabled:opacity-50"
            >
              <UserCheck size={18} />
              {seeding ? ep('unlink_seeding', { defaultValue: 'جاري إنشاء بيانات تجريبية…' }) : ep('unlink_seed_btn', { defaultValue: 'إنشاء بيانات تجريبية عشوائية' })}
            </button>
          ) : null}
          <Link
            to="/employees"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/25 text-white/90 hover:bg-white/10 text-sm font-medium"
          >
            {ep('unlink_hr_link')}
          </Link>
        </div>
      )}
      {linkError ? (
        <p className="mt-4 text-red-300 text-sm">{linkError}</p>
      ) : null}
    </div>
  );
}
